import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatMoney,
  formatServiceDay,
  latestPerMeal,
  MEAL_LABELS,
  type MyOrder,
} from '@mealdesk/shared';
import CancelOrder from '../components/CancelOrder.js';
import { EmptyState, MealChip } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { mealTone } from '../lib/meals.js';
import {
  useHistory,
  useMoney,
  useMonthly,
  useTodayOrders,
  useUpcoming,
} from '../lib/ordering.js';

/**
 * What you ordered, where it is going, and which meal it is.
 *
 * It used to open on the month's money, which for a company that pays in full
 * is the figure zero, every month, forever. What somebody actually opens this
 * screen for is the next meal they booked and where to collect it — so that is
 * the panel at the top, and the money is a line further down.
 */
export default function HistoryPage() {
  const orders = useHistory();
  const today = useTodayOrders();
  const upcoming = useUpcoming();
  const monthly = useMonthly();
  const { t } = useT();

  const monthLabel = new Date(`${monthly.month}-01T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
  });

  // The one to show at the top: what is being eaten soonest.
  const next = today[0] ?? upcoming[0];
  const later = upcoming.filter((o) => o.id !== next?.id);

  const day = new Date().toLocaleDateString('en-CA');
  // One row per meal — the order that stood, not the ones it replaced — and
  // newest first, as the API lists them.
  const past = useMemo(
    () =>
      latestPerMeal(
        orders.filter((o) => o.serviceDate.startsWith(monthly.month) && o.serviceDate <= day),
      ),
    [orders, monthly.month, day],
  );

  // The last few meals are what somebody checks; the rest of the month is one
  // tap away rather than a scroll through every lunch since the 1st.
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? past : past.slice(0, RECENT);

  // A cancelled booking leaves the list it was in, so say that it went.
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);
  const cancelled = () => setNotice(t('Order cancelled. You can order that meal again before the cutoff.'));

  return (
    <>
      <div className="px-[22px] pt-[calc(22px+env(safe-area-inset-top))] md:px-2 md:pt-7">
        <h2 className="text-[25px] font-extrabold md:text-[30px]">{t('My orders')}</h2>

        {notice && (
          <p
            role="status"
            className="mt-3 rounded-tile bg-ok-soft px-4 py-3 text-[13.5px] font-bold text-ok-ink md:text-[15px]"
          >
            {notice}
          </p>
        )}

        {next ? (
          <div className="mt-4 rounded-panel border border-ok-line bg-brand-soft px-[22px] pt-[22px] pb-5 md:px-8 md:pt-7 md:pb-6">
            <span className="text-[13px] font-bold tracking-[0.04em] text-subtle uppercase md:text-[14.5px]">
              {t('Next up')}
            </span>

            <b className="mt-[3px] block text-[25px] tracking-[-0.04em] md:text-[30px]">
              {capitalise(formatServiceDay(next.serviceDate))} · {t(MEAL_LABELS[next.mealType])}
            </b>

            <span className="mt-1.5 block text-[16px] md:text-[17px]">{next.title}</span>

            {/* Where it is left. The one fact that decides whether you get it. */}
            <span
              className={`mt-1 block text-[13.5px] md:text-[15px] ${
                next.deliveryPointName ? 'text-muted' : 'font-bold text-warn-ink'
              }`}
            >
              {next.deliveryPointName
                ? t('Collect at {place}', { place: next.deliveryPointName })
                : t('No pickup point — set one in your profile')}
            </span>

            {/* The meal somebody is most likely to change their mind about is
                the next one, so it can be taken back from right here. */}
            <div className="mt-4 empty:hidden">
              <CancelOrder order={next} onCancelled={cancelled} />
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <EmptyState
              title={t('Nothing booked')}
              body={t('Days you order appear here with what you ordered and where to collect it.')}
            />
          </div>
        )}
      </div>

      {later.length > 0 && (
        <div className="px-[22px] pt-[22px] md:px-2 md:pt-7">
          <b className="mb-[11px] block text-[13px] font-extrabold tracking-[0.07em] text-subtle uppercase md:text-[14.5px]">
            {t('Booked ahead')} · {later.length}
          </b>
          <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3.5">
            {later.map((order) => (
              <OrderRow key={order.id} order={order} onCancelled={cancelled} />
            ))}
          </div>
        </div>
      )}

      <div className="px-[22px] pt-[22px] md:px-2 md:pt-7">
        <b className="mb-[11px] block text-[13px] font-extrabold tracking-[0.07em] text-subtle uppercase md:text-[14.5px]">
          {monthLabel}
        </b>

        {past.length === 0 && (
          <EmptyState
            title={t('Nothing yet this month')}
            body={t('Meals you have eaten appear here.')}
          />
        )}

        <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3.5">
          {shown.map((order) => (
            <OrderRow key={order.id} order={order} onCancelled={cancelled} />
          ))}
        </div>

        {past.length > RECENT && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="mt-2.5 flex h-11 w-full items-center justify-center rounded-control bg-white text-[14.5px] font-extrabold text-brand-ink md:text-[16px]"
          >
            {expanded
              ? t('Show fewer')
              : t('Show earlier orders ({count})', { count: past.length - RECENT })}
          </button>
        )}

        {/* The money, kept but demoted: under a full subsidy it is zero every
            month, and a headline that is always zero teaches people to skip it. */}
        <div className="mt-3.5 flex items-center rounded-tile bg-white px-[17px] py-[15px] shadow-[0_4px_14px_rgba(38,64,28,.04)]">
          <span className="text-[13.5px] text-muted md:text-[15px]">
            {monthly.mealCount} meal{monthly.mealCount === 1 ? '' : 's'} ·{' '}
            {monthly.total === 0 ? t('company paid in full') : t('Your share')}
          </span>
          <b className="ml-auto text-[17px] md:text-[18.5px]">
            {formatMoney(monthly.total, monthly.currency)}
          </b>
        </div>
      </div>
    </>
  );
}

/** How many past meals are shown before "show earlier". */
const RECENT = 5;

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const STATUS: Record<MyOrder['status'], { label: string; className: string }> = {
  placed: { label: 'Ordered', className: 'bg-ok-soft text-ok-ink' },
  confirmed: { label: 'Confirmed', className: 'bg-ok-soft text-ok-ink' },
  delivered: { label: 'Delivered', className: 'bg-neutral-soft text-neutral-ink' },
  cancelled: { label: 'Cancelled', className: 'bg-danger-soft text-danger-ink' },
  cancelled_exception: { label: 'Exception', className: 'bg-danger-soft text-danger-ink' },
};

/*
 * A card, not a link: the cancel control lives inside it, and a button inside
 * a link is two things claiming the same tap. The details part links; the
 * control below it does not.
 */
function OrderRow({ order, onCancelled }: { order: MyOrder; onCancelled: () => void }) {
  const { t } = useT();
  const chip = STATUS[order.status];
  const tone = mealTone(order.mealType);
  const money = useMoney()(order.price, order.employeeShare, order.currency);

  return (
    <div className="rounded-tile bg-white px-[17px] py-[15px] shadow-[0_4px_14px_rgba(38,64,28,.04)]">
      <Link to={`/confirm/${order.id}`} className="block">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <MealChip mealType={order.mealType} when={capitalise(formatServiceDay(order.serviceDate))} />
          <span
            className={`shrink-0 rounded-pill px-2.5 py-1 text-[13px] font-extrabold md:text-[14.5px] ${chip.className}`}
          >
            {t(chip.label)}
          </span>
        </div>

        <b className={`block text-[15px] tracking-[-0.03em] text-pretty md:text-[16.5px] ${tone.text}`}>
          {order.title}
        </b>

        <span className="mt-0.5 block text-[13px] text-muted md:text-[14.5px]">
          {order.restaurantName}
          {money ? ` · ${money}` : ''}
        </span>

        {/* An order nobody can say the pickup point for is an order the kitchen
            has nowhere to leave. */}
        <span
          className={`mt-0.5 block text-[13px] md:text-[14.5px] ${
            order.deliveryPointName ? 'text-subtle' : 'font-bold text-warn-ink'
          }`}
        >
          {order.deliveryPointName || t('No pickup point — set one in your profile')}
        </span>
      </Link>

      <div className="mt-3 empty:hidden">
        <CancelOrder order={order} onCancelled={onCancelled} />
      </div>
    </div>
  );
}
